"use client";

import React from "react";
import { X } from "lucide-react";
import { LiquidPill, type LiquidPillProps } from "./liquid-pill";

export interface LiquidPillCloseableProps extends Omit<LiquidPillProps, "children"> {
  /** Callback cuando se hace click en el botón close (X) */
  onClose: () => void;
  /** Mostrar el botón de close */
  showClose?: boolean;
}

/**
 * LiquidPillCloseable
 * 
 * Wrapper de LiquidPill que renderiza un botón close (X) adentro.
 * Útil para pills que son tags/filtros que pueden ser removidos.
 * 
 * Ejemplo:
 * ```tsx
 * <LiquidPillCloseable
 *   text="React"
 *   onClose={() => handleRemoveTag("React")}
 *   variant="indigo"
 *   size="sm"
 * />
 * ```
 */
export const LiquidPillCloseable = React.forwardRef<
  HTMLDivElement,
  LiquidPillCloseableProps
>(function LiquidPillCloseable(
  {
    onClose,
    showClose = true,
    text,
    icon,
    variant,
    appearance,
    shape = "pill",
    size = "sm",
    ...props
  },
  ref,
) {
  return (
    <LiquidPill
      ref={ref}
      variant={variant}
      appearance={appearance}
      shape={shape}
      size={size}
      {...props}
    >
      <span className="flex items-center gap-1">
        {icon && <span className="flex shrink-0 items-center leading-none">{icon}</span>}
        {text && <span className="whitespace-nowrap">{text}</span>}
        {showClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full hover:opacity-70 active:scale-90"
            aria-label="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    </LiquidPill>
  );
});

LiquidPillCloseable.displayName = "LiquidPillCloseable";
